import { useState, type ChangeEvent, type FormEvent } from "react";
import { Alert, Button, Card, Container, Form } from "@openedx/paragon";
type Role = "learner" | "author" | "admin";
type User = { id: string; name: string; email: string; role: Role };

const roleLabels: Record<Role, string> = { learner: "Learner", author: "Author", admin: "Administrator" };
const navByRole: Record<Role, string[]> = {
  learner: ["Home", "My learning", "Discover"],
  author: ["Overview", "Courses", "Drafts"],
  admin: ["Overview", "People", "Audit log"]
};
type PreviewSession = User & { preview?: boolean };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init, credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) }
  });
  const body = (await response.json()) as T & { error?: { message: string } };
  if (!response.ok) throw new Error(body.error?.message ?? "Request failed");
  return body;
}

function Login({ onSignedIn }: { onSignedIn: (user: PreviewSession) => void }) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("hello@kademie.org");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const body = mode === "register" ? { name, email, password } : { email, password };
      const result = await request<{ user: User }>(`/api/v1/auth/${mode}`, { method: "POST", body: JSON.stringify(body) });
      onSignedIn(result.user);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to sign in.");
    } finally { setBusy(false); }
  }

  return (
    <div className="pgn-auth-page">
      <header className="pgn-auth-header">
        <Container size="lg" className="pgn-auth-header-inner">
          <a className="pgn-brand" href="/" aria-label="Kademie home"><span className="pgn-brand-mark">K</span><span>Kademie</span></a>
          <span className="pgn-header-help">Learning that moves with you</span>
        </Container>
      </header>
      <main className="pgn-auth-main">
        <Container size="lg">
          <div className="pgn-auth-grid">
            <section className="pgn-auth-intro" aria-labelledby="auth-intro-title">
              <span className="pgn-eyebrow">Kademie learning platform</span>
              <h1 id="auth-intro-title">Learn with a clear path forward.</h1>
              <p>Focused tools for people who teach, learn, and build practical progress together.</p>
              <ul className="pgn-auth-benefits">
                <li><span aria-hidden="true">✓</span> Keep learning in one focused workspace</li>
                <li><span aria-hidden="true">✓</span> Pick up where you left off</li>
                <li><span aria-hidden="true">✓</span> Build progress you can see</li>
              </ul>
            </section>
            <Card className="pgn-auth-card" aria-labelledby="auth-title">
              <Card.Body>
                <div className="pgn-card-heading">
                  <span className="pgn-eyebrow">Your workspace</span>
                  <h2 id="auth-title">{mode === "login" ? "Sign in to Kademie" : "Create your account"}</h2>
                  <p className="pgn-muted">{mode === "login" ? "Use your account to continue." : "Start with a learner account."}</p>
                </div>
                <Form onSubmit={submit} noValidate>
                  {mode === "register" && <Form.Group controlId="auth-name" className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control required minLength={2} value={name} onChange={(event: ChangeEvent<HTMLInputElement>) => setName(event.target.value)} />
                  </Form.Group>}
                  <Form.Group controlId="auth-email" className="mb-3">
                    <Form.Label>Email address</Form.Label>
                    <Form.Control required type="email" value={email} onChange={(event: ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)} />
                  </Form.Group>
                  <Form.Group controlId="auth-password" className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control required minLength={8} type="password" value={password} onChange={(event: ChangeEvent<HTMLInputElement>) => setPassword(event.target.value)} />
                  </Form.Group>
                  {error && <Alert variant="danger" className="pgn-form-alert" role="alert">{error}</Alert>}
                  <Button type="submit" variant="primary" size="lg" className="w-100" disabled={busy}>{busy ? "Working…" : mode === "login" ? "Sign in" : "Create account"}</Button>
                </Form>
                <div className="pgn-auth-links">
                  <Button variant="link" className="pgn-link-button" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>{mode === "login" ? "Create a new account" : "Already have an account? Sign in"}</Button>
                  <Button variant="secondary" className="w-100" onClick={() => onSignedIn({ id: "preview", name: "Preview learner", email: "preview@kademie.local", role: "learner", preview: true })}>Explore the product shell</Button>
                </div>
                <p className="pgn-preview-note">Preview mode lets you explore the Phase 1 interface without creating an account.</p>
              </Card.Body>
            </Card>
          </div>
        </Container>
      </main>
      <footer className="pgn-auth-footer">© {new Date().getFullYear()} Kademie · Learning that moves with you</footer>
    </div>
  );
}

function AppShell({ session, onSignOut }: { session: PreviewSession; onSignOut: () => void }) {
  const [active, setActive] = useState(navByRole[session.role][0]);
  const navItems = navByRole[session.role];
  async function signOut() {
    if (!session.preview) await request("/api/v1/auth/logout", { method: "POST" }).catch(() => undefined);
    onSignOut();
  }
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-lockup"><span className="brand-glyph">K</span><span>Kademie</span></div>
        <div className="workspace-switcher"><span className="workspace-dot" /> Kademie workspace <span>⌄</span></div>
        <nav aria-label="Workspace navigation">
          <p className="nav-label">Workspace</p>
          {navItems.map((item) => <button className={active === item ? "nav-item active" : "nav-item"} key={item} onClick={() => setActive(item)}><span className="nav-icon">{item === "Home" || item === "Overview" ? "▦" : item === "Discover" ? "⌕" : "◈"}</span>{item}</button>)}
        </nav>
        <div className="sidebar-bottom"><button className="nav-item"><span className="nav-icon">?</span> Help center</button><button className="nav-item" onClick={signOut}><span className="nav-icon">↪</span> Sign out</button></div>
      </aside>
      <main className="workspace">
        <header className="topbar"><div className="breadcrumbs"><span>Kademie</span><span>/</span><strong>{active}</strong></div><div className="topbar-actions"><button className="icon-button" aria-label="Notifications">♧</button><div className="avatar">{session.name.charAt(0)}</div></div></header>
        <div className="content">
          <div className="page-heading"><div><span className="eyebrow">{roleLabels[session.role]} workspace</span><h1>{active}</h1><p className="muted">{session.preview ? "You are exploring the Kademie product shell in preview mode." : `Good to see you, ${session.name.split(" ")[0]}.`}</p></div><button className="button primary">Create new</button></div>
          <section className="hero-panel"><div><span className="eyebrow light">PHASE 1 FOUNDATION</span><h2>A calm place to make progress.</h2><p>Clear roles, focused navigation, and a foundation ready for the first course workflow.</p><button className="button light-button">View foundation status <span>→</span></button></div><div className="hero-shape" aria-hidden="true"><span>✦</span><span>↗</span><span>○</span></div></section>
          <div className="section-heading"><div><h2>Workspace overview</h2><p className="muted">The building blocks are in place for the next vertical slice.</p></div><button className="link-button">See all →</button></div>
          <section className="metric-grid">
            <article className="metric-card"><span className="metric-icon coral">◎</span><span className="metric-label">Your role</span><strong>{roleLabels[session.role]}</strong><small>Permission-aware workspace</small></article>
            <article className="metric-card"><span className="metric-icon green">✓</span><span className="metric-label">Foundation</span><strong>Ready</strong><small>Auth, roles, API, and audit trail</small></article>
            <article className="metric-card"><span className="metric-icon blue">↗</span><span className="metric-label">Next milestone</span><strong>Course flow</strong><small>Create → publish → learn</small></article>
          </section>
          <section className="activity-card"><div className="section-heading"><div><h2>Recent activity</h2><p className="muted">A transparent record of meaningful workspace events.</p></div><span className="status-pill">● Live foundation</span></div><div className="empty-state"><span className="empty-icon">✦</span><h3>Your workspace is ready</h3><p>Course activity will appear here when the next phase is connected.</p></div></section>
        </div>
      </main>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState<PreviewSession | null>(null);
  return session ? <AppShell session={session} onSignOut={() => setSession(null)} /> : <Login onSignedIn={setSession} />;
}
